import { Link, useLocation } from 'react-router-dom';
import { CircleHelp } from 'lucide-react';
import type { OrgType } from '@ready/shared';
import { findGuide, guidePath } from '@/content/guides';

/*
 * "Help with this page": a link from an org page to the chapter that covers
 * it, picked by the page's section (/organizations/:orgId/<section>/…), in
 * the guide for the org's type (the general guide otherwise).
 */
const GUIDE_FOR_TYPE: Partial<Record<OrgType, string>> = {
  university: 'universities',
  school: 'universities',
  nonprofit: 'nonprofits',
  enterprise: 'companies',
  community: 'communities',
};

const ORG_SECTIONS: Record<string, string> = {
  '': 'at-a-glance',
  members: 'people',
  invites: 'people',
  groups: 'people',
  'library/courses': 'build-a-course',
  'library/quizzes': 'assessments',
  'library/question-banks': 'assessments',
  'library/events': 'events',
  library: 'make-it-yours',
  assignments: 'assign',
  reports: 'impact',
  profile: 'set-up',
  settings: 'set-up',
  children: 'set-up',
  feedback: 'support',
  help: 'support',
};

function orgChapter(pathname: string): string | undefined {
  const rest = pathname.split('/').slice(3).join('/');
  const key = Object.keys(ORG_SECTIONS)
    .filter((k) => rest === k || rest.startsWith(`${k}/`) || k === '')
    .sort((a, b) => b.length - a.length)[0];
  return key === undefined ? undefined : ORG_SECTIONS[key];
}

export function OrgPageHelp({ type }: { type: OrgType }) {
  const { pathname } = useLocation();
  const chapter = orgChapter(pathname);
  const guide = findGuide(GUIDE_FOR_TYPE[type]) ?? findGuide('organizations');
  const c = guide?.chapters.find((x) => x.slug === chapter);
  if (!guide || !c) return null;
  return (
    <Link
      to={guidePath(guide.slug, c.slug)}
      title={`Guide: ${c.title}`}
      aria-label={`Help with this page: ${c.title}`}
      className="-my-2 ml-auto inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <CircleHelp className="size-5" aria-hidden />
      <span className="hidden sm:inline">Help with this page</span>
    </Link>
  );
}
